import MomentUtils from '@date-io/moment';
import { CssBaseline } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider } from 'material-ui-pickers';
import React, { Suspense } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { Provider } from 'react-redux';
import { Redirect } from 'react-router';
import { HashRouter, Route, Switch } from 'react-router-dom';

import { identify, loadAnalytics, page } from '~/src/dash/analytics/analytics';
import { getSmallSites } from '~/src/dash/api/channels/channels_methods';
import {
  getHeaderBidderParamOptions,
  getPublisher,
  getSession,
  getUINotices,
  IDemandSourceNameMap,
  IHeaderBidderParamOption,
  IPublisher,
  IUINotice,
  IUser,
} from '~/src/dash/api/console-api';
import { getPermissionTemplate } from '~/src/dash/api/permissions/service';
import { EBool } from '~/src/dash/api/types/bool';
import { theme } from '~/src/dash/mui/theme';
import { SESSION_ACTIONS_SET } from '~/src/dash/reducers/session-reducers';
import { IStore, store } from '~/src/dash/reducers/store';
import { IUserState, USER_ACTIONS_SET } from '~/src/dash/reducers/user-reducers';
import {
  getDemoSSAIUsername,
  isIntegralplatform,
} from '~/src/dash/resellers/utils/importResellerConfig';
import { authenticate } from '~/src/dash/router/auth';
import {
  extractPublisherIDFromRoute,
  getRoutePath,
  getRouteTitle,
  IInventoryRoute,
  isPathV2,
  navigate,
  navigateError,
  parseLocation,
  reload,
  RouteAccountSettings,
  RouteAudienceDataShareManager,
  RouteAudienceManagerCombineSegments,
  RouteAudienceManagerDataProviderSettings,
  RouteAudienceManagerLiverampMarketplace,
  RouteAudienceManagerSegmentExisting,
  RouteAudienceManagerSegmentNew,
  RouteAudienceManagerSettingsList,
  RouteBidderBulkUpdate,
  RouteBidderBulkUpload,
  RouteBidderSettingsClone,
  RouteBidderSettingsExisting,
  RouteBidderSettingsList,
  RouteBidderSettingsNew,
  RouteBillingDetailConfigurationExisting,
  RouteBillingDetailConfigurationNew,
  RouteBillingDetailList,
  RouteBillingDetailTiersExisting,
  RouteBillingDetailTiersNew,
  RouteBrandSafetyEdit,
  RouteBrandSafetyList,
  RouteBrandSafetyNew,
  RouteBrandSafetyTrackerEdit,
  RouteBrandSafetyTrackerNew,
  RouteBrandsDashboard,
  RouteCampaignCopy,
  RouteCampaignExisting,
  RouteCampaignNew,
  RouteCampaignSettingsList,
  RouteChannelBulkUpdate,
  RouteChannelExisting,
  RouteChannelGroupBulkUpdate,
  RouteChannelNew,
  RouteChannelsView,
  RouteConfigurations,
  RouteContextualSegmentExisting,
  RouteContextualSegmentNew,
  RouteContextualSegmentSettingsList,
  RouteCreativesLibraryList,
  RouteDashboards,
  RouteDealClone,
  RouteDealCreate,
  RouteDealEdit,
  RouteDealList,
  RouteDealNew,
  RouteDealTPAccountList,
  RouteForecasting,
  RouteFreewheelSiteEdit,
  RouteFreewheelSiteList,
  RouteFreewheelSiteNew,
  RouteIABCategoryManager,
  RouteIASDashboard,
  RouteIASGlossary,
  RouteInventoryForecast,
  RouteInventoryRouteEdit,
  RouteInventoryRouteList,
  RouteInventoryRouteNew,
  RouteInventoryRules,
  RouteIPTraceList,
  RouteIPTraceNew,
  RouteMeasurementDashboard,
  RouteMeasurementDrill,
  RouteMobileHome,
  RouteMonitorBulkUpdate,
  RouteMonitorBulkUpload,
  RouteMonitorEdit,
  RouteMonitorList,
  RouteMonitorNew,
  RouteNetworkDashboard,
  RouteNetworkList,
  RouteNetworkSettingsClone,
  RouteNetworkSettingsExisting,
  RouteNetworkSettingsNew,
  RouteOrderList,
  RouteOrderSettingsClone,
  RouteOrderSettingsExisting,
  RouteOrderSettingsNew,
  RoutePlatformConnectOfferNew,
  RoutePlatformConnects,
  RoutePublisherChangeLogs,
  RoutePublisherSettings,
  RouteReportBuilderExisting,
  RouteReportBuilderList,
  RouteReportBuilderNew,
  RouteReportExisting,
  RouteReportNew,
  RouteReportsDashboard,
  RouteReportsList,
  RouteResellerExisting,
  RouteResellerManagerList,
  RouteResellerNew,
  RouteSandbox,
  RouteAdVisor,
  RouteSegmentsLiverampDistribution,
  RouteSSPReportExisting,
  RouteSSPReportList,
  RouteSSPReportNew,
  RouteTargetingList,
  RouteTargetingPresetClone,
  RouteTargetingPresetExisting,
  RouteTargetingPresetNew,
  RouteTeamSettings,
  RouteInventoryRulesList,
  RouteAdBreaksList,
  RouteContentMetadataList,
  RouteCustomDataList,
} from '~/src/dash/router/routes';
import { IHistoryProps, ISessionStateProps, IUserStateProps } from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { PermissionService } from '~/src/dash/utils/permission_service';
import { isLiverampIntegrationEnabled, PID } from '~/src/dash/utils/publishers';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import {
  isAudienceRouteDisabled,
  isCampaignRouteDisabled,
  isDealRouteDisabled,
  isDemoAccount,
  isForecastingAllowed,
  isPlatformConnectDisabled,
  shouldDisplayInventoryRules,
} from '~/src/dash/utils/ui';
import { AccountSettingsView } from '~/src/dash/views/account-settings-view';
import { AppErrorView } from '~/src/dash/views/app-error-view';
import { AudienceDataShareListView } from '~/src/dash/views/audience-data-share-list';
import { AudienceManagerDataProviderSettings } from '~/src/dash/views/audience-manager-data-provider-settings';
import { AudienceManagerCombineSegmentsView } from '~/src/dash/views/audience-manager-segment-combine-view';
import { AudienceManagerSegmentExistingView } from '~/src/dash/views/audience-manager-segment-existing-view';
import { AudienceManagerSegmentNewView } from '~/src/dash/views/audience-manager-segment-new-view';
import { BaseClass } from '~/src/dash/views/base-view';
import { BidderBulkUploadView } from '~/src/dash/views/bidder-bulk-upload-view';
import { BidderSettingsExistingView } from '~/src/dash/views/bidder-settings-existing-view';
import { BidderSettingsNewView } from '~/src/dash/views/bidder-settings-new-view';
import { BidderSiteNewView } from '~/src/dash/views/bidder-site-new-view';
import { Bidders } from '~/src/dash/views/bidders/Bidders';
import { BillingDetailConfigurationSettingsView } from '~/src/dash/views/billing-details/billing-detail-configuration-settings-view';
import { BillingDetailTiersSettingsView } from '~/src/dash/views/billing-details/billing-detail-tiers-settings-view';
import { BillingDetailListView } from '~/src/dash/views/billing-details/billing-details-list-view';
import { BrandSafetyEditView } from '~/src/dash/views/brand-safety-edit-view';
import { BrandSafetyListView } from '~/src/dash/views/brand-safety-list-view';
import { BrandSafetyNewView } from '~/src/dash/views/brand-safety-new-view';
import { BrandSafetyTrackerEditView } from '~/src/dash/views/brand-safety-tracker-edit-view';
import { BrandSafetyTrackerNewView } from '~/src/dash/views/brand-safety-tracker-new-view';
import { BrandsDashboard } from '~/src/dash/views/brands-dashboard/BrandsDashboard';
import { BulkUpdateChannelGroups } from '~/src/dash/views/bulk-update-channel-groups/BulkUpdateChannelGroups';
import { CampaignCopyView } from '~/src/dash/views/campaign/campaign-copy-view';
import { CampaignNewView } from '~/src/dash/views/campaign/campaign-new-view';
import { LineItemContainer } from '~/src/dash/views/campaign/LineItem/container';
import { ChannelsView } from '~/src/dash/views/channels';
import { ConfigurationsSettingsView } from '~/src/dash/views/configurations-settings-view';
import { ConsoleDownView } from '~/src/dash/views/console-down-view';
import { ContextualSegmentSettingsNewView } from '~/src/dash/views/contextual-segments/contextual-segment-new-view';
import { ContextualSegmentSettingsExistingView } from '~/src/dash/views/contextual-segments/contextual-segment-settings-existing-view';
import { ContextualSegmentSettingsListView } from '~/src/dash/views/contextual-segments/contextual-segment-settings-list-view';
import { CreativeLibraryListView } from '~/src/dash/views/creatives/creative-library-list-view';
import { DashboardHome } from '~/src/dash/views/dashboards/DashboardHome';
import { DealCreateView } from '~/src/dash/views/deal/deal-create-view';
import { DealsListView } from '~/src/dash/views/dealV2/DealsList';
import { DealSettingsView } from '~/src/dash/views/deal/deal-settings-view';
import { DealTPAccountView } from '~/src/dash/views/deal/deal-tp-account-view';
import { ErrorBoundary } from '~/src/dash/views/error-boundary';
import { FreewheelListView } from '~/src/dash/views/freewheel/freewheel-list-view';
import { FreewheelSettingsView } from '~/src/dash/views/freewheel/freewheel-settings-view';
import { IABCategoryManagerView } from '~/src/dash/views/iab-category-manager-view';
import { IASDashboard } from '~/src/dash/views/ias-publisher/IASDashboard';
import { IASGlossary } from '~/src/dash/views/ias-publisher/IASGlossary';
import { IasReportView } from '~/src/dash/views/ias-publisher/IASReportBuilder/Report';
import { ReportsDashboard } from '~/src/dash/views/ias-publisher/IASReportsDashboard/ReportsDashboard';
import { InventoryForecastView } from '~/src/dash/views/inventory-forecast-view';
import { InvitationAcceptionView } from '~/src/dash/views/invitation-acception-view';
import { IPTraceListView } from '~/src/dash/views/ip-trace/ip-trace-list-view';
import { IPTraceNewView } from '~/src/dash/views/ip-trace/ip-trace-new-view';
import { LoadingView } from '~/src/dash/views/loading-view';
import { MeasurementDrill } from '~/src/dash/views/measurement/MeasurementDrill';
import { MeasurementHomeView } from '~/src/dash/views/measurement-home-view';
import { MobileBaseView } from '~/src/dash/views/mobile/MobileBaseView';
import { MobileDashboard } from '~/src/dash/views/mobile/MobileBaseView/Home';
import { MonitorBulkUploadView } from '~/src/dash/views/monitor/monitor-bulk-upload-view';
import { MonitorExistingView } from '~/src/dash/views/monitor/monitor-existing-view';
import { MonitorListView } from '~/src/dash/views/monitor/monitor-list-view';
import { MonitorNewView } from '~/src/dash/views/monitor/monitor-new-view';
import { NetworkListView } from '~/src/dash/views/networks/network-list-view';
import { NetworkSettingsCloneView } from '~/src/dash/views/networks/network-settings-clone-view';
import { NetworkSettingsExistingView } from '~/src/dash/views/networks/network-settings-existing-view';
import { NetworkHomeView } from '~/src/dash/views/networks/network-settings-home-view';
import { NetworkSettingsNewView } from '~/src/dash/views/networks/network-settings-new-view';
import { OrderSettingsView } from '~/src/dash/views/orders/order-settings-view';
import { PasswordRecoverView } from '~/src/dash/views/password-recover-view';
import { PasswordResetView } from '~/src/dash/views/password-reset-view';
import { PlatformConnectView } from '~/src/dash/views/platform-connect';
import { PlatformConnectOfferNewContainer } from '~/src/dash/views/platform-connect/DraftOfferNew/container';
import { PublisherChangeLogsView } from '~/src/dash/views/publisher/change-logs/publisher-change-logs-view';
import { PublisherBaseView } from '~/src/dash/views/publisher-base-view';
import { PublisherSettingsView } from '~/src/dash/views/publisher-settings-view';
import { ReportPreviewContainer } from '~/src/dash/views/reporting/Preview/container';
import { ReportBuilderNew } from '~/src/dash/views/reporting/ReportBuilder';
import { ReportBuilderExisting } from '~/src/dash/views/reporting/ReportBuilderExisting';
import { ResellerExistingView } from '~/src/dash/views/reseller-existing-view';
import { ResellerListView } from '~/src/dash/views/reseller-list-view';
import { ResellerNewView } from '~/src/dash/views/reseller-new-view';
import { RouteSettingsExistingView } from '~/src/dash/views/route/route-settings-existing-view';
import { RouteSettingsNewView } from '~/src/dash/views/route/route-settings-new-view';
import { RouteSettingsListView } from '~/src/dash/views/routes-settings-list-view';
import { Sandbox } from '~/src/dash/views/sandbox/Sandbox';
import { AdVisor } from '~/src/dash/views/ai-assistant/AdVisor';
import { AudienceSegments } from '~/src/dash/views/segments/audience/AudienceSegments';
import { ManageLiveRampDistribution } from '~/src/dash/views/segments/audience/liveramp/distribution/manage-liveramp-distribution';
import { LiverampMarketplaceSegmentsListView } from '~/src/dash/views/segments/audience/liveramp/marketplace';
import { SigninView } from '~/src/dash/views/signin-view';
import { SiteSettingsListView } from '~/src/dash/views/site-settings-list-view';
import { SSPReportExistingView } from '~/src/dash/views/ssp-report-existing-view';
import { SSPReportListView } from '~/src/dash/views/ssp-report-list-view';
import { SSPReportNewView } from '~/src/dash/views/ssp-report-new-view';
import { TeamSettingsView } from '~/src/dash/views/team-settings-view';
import { ForecastingView } from '~/src/dash/views/tools/Forecasting';
import { TargetingPresets } from '~/src/dash/views/tools/TargetingPresets';
import { TargetingPresetFormView } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView';
import { ReportingListView } from '~/src/dash/views/reporting/ReportingListView';
import { InventoryRulesView } from '~/src/dash/views/inventory-rules';
import { LineItemsListView } from '~/src/dash/views/campaigns/line-items/LineItemsListView';
import { OrdersListView } from '~/src/dash/views/campaigns/orders/OrdersListView';
import { InventoryRulesList } from '~/src/dash/views/inventory-rules-v2/Rules/index';
import { AdBreaksList } from '~/src/dash/views/inventory-rules-v2/AdBreaks';
import { ContentMetadataList } from '~/src/dash/views/inventory-rules-v2/ContentMetadata';
import { CustomDataList } from '~/src/dash/views/inventory-rules-v2/CustomData';
// TODO: update these imports when deal views are ready to be used on production
// import { DealExisting } from '~/src/dash/views/dealV2/DealExisting';
// import { DealNew } from '~/src/dash/views/dealV2/DealNew';
// import { DealCreate } from '~/src/dash/views/dealV2/DealCreate';

const { resellerID } = (window as any).CONFIG;

const isMobileView = () => {
  // TODO: UPDATE THIS WHEN READY TO BE USED ON WHITELABELS
  // THIS BLOCKS WHITELABELS FROM USING THE MOBILE VIEW
  if (resellerID !== -1 || sessionStorage.getItem('forceDesktop') === 'true') {
    return false;
  } else {
    return isMobileOnly;
  }
};

const SessionUnknown = 0;
const SessionIntrospect = 1;
const SessionInactive = 2;
const SessionActive = 3;

interface IProps extends IHistoryProps, ISessionStateProps, IUserStateProps {
  updateConsoleDown: VoidFunction;
}

const replaceSSAILandingRoute = (user: IUser, defaultRoute: IInventoryRoute): IInventoryRoute => {
  return getDemoSSAIUsername() === user.Username ? RouteNetworkDashboard : defaultRoute;
};

const AppRouterView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    router: true,
  },
  class extends React.Component<IProps> {
    state = {
      sessionActive: SessionUnknown,
    };

    loadRoute = (userData: {
      user: IUser;
      publisher: IPublisher;
      demandSourceNameMap: IDemandSourceNameMap;
    }) => {
      ((): Promise<any[]> => {
        const loc = parseLocation(this.props.location.pathname);
        if (!loc.publisher) {
          return Promise.reject(new Error('unknown publisher'));
        }
        return getPublisher({
          query: {
            publisher_id: loc.publisher,
          },
        }).then((publisher: IPublisher) => {
          userData.publisher = publisher;
          this.props.dispatch(
            SESSION_ACTIONS_SET({
              publisher,
            }),
          );
          return getSmallSites(loc.publisher || '');
        });
      })()
        .then((response: any[]) => {
          const [sites, err] = response;
          if (err != null) {
            throw new Error('error fetching sites');
          }
          if (this.props.location.pathname.indexOf('bidder') > -1) {
            if (sites === undefined || sites.length === 0) {
              navigate(RouteChannelNew, {
                p: userData.publisher.ID,
              });

              this.props.dispatch(USER_ACTIONS_SET(userData));
              this.props.dispatch(
                SESSION_ACTIONS_SET({
                  site: undefined,
                }),
              );

              // Load permissions
              getPermissionTemplate(parseInt(userData.publisher.ID, 10))
                .then(([permissionTemplate, err]) => {
                  if (err === undefined) {
                    this.props.dispatch(
                      USER_ACTIONS_SET({ permissionTemplate } as Partial<IUserState>),
                    );
                    if (permissionTemplate !== undefined) {
                      PermissionService.getInstance().setTemplate(permissionTemplate);
                    }
                  } else {
                    console.error("Can't get permissions ", err);
                  }
                  this.setState({
                    sessionActive: SessionActive,
                  });
                })
                .catch(() => {
                  this.setState({
                    sessionActive: SessionActive,
                  });
                });

              return;
            }
          }
          this.setSession(userData, userData.publisher, userData.demandSourceNameMap);
        })
        .catch(() => {
          reload(
            isIntegralplatform()
              ? RouteIASDashboard
              : replaceSSAILandingRoute(userData.user, RouteDashboards),
            {
              p: userData.publisher.ID,
            },
          );
        });
    };

    getUINotices = (publisherID: string): void => {
      getUINotices({
        query: {
          publisher_id: publisherID,
        },
      })
        .then((uiNotices: IUINotice[]) => {
          this.props.dispatch(
            USER_ACTIONS_SET({
              uiNotices: uiNotices || [],
            }),
          );
        })
        .catch(() => {
          this.props.dispatch(
            USER_ACTIONS_SET({
              uiNotices: [] as IUINotice[],
            }),
          );
        });
    };

    getSession = () => {
      const publisherID = extractPublisherIDFromRoute(this.props.history.location.pathname);
      return getSession({
        query: {
          publisher_id: publisherID,
        },
      });
    };

    loadSession = (
      fn?: (userData: {
        user: IUser;
        publisher: IPublisher;
        demandSourceNameMap: IDemandSourceNameMap;
      }) => void,
    ): void => {
      if (this.state.sessionActive === SessionIntrospect) {
        return;
      }
      if (this.state.sessionActive === SessionActive) {
        this.getSession()
          .then(
            (userData: {
              user: IUser;
              publisher: IPublisher;
              demandSourceNameMap: IDemandSourceNameMap;
            }) => {
              const publisherID = parseLocation(this.props.location.pathname).publisher;
              if (publisherID !== undefined) {
                this.getUINotices(publisherID);
              } else {
                this.getUINotices(userData.publisher.ID);
              }
              if (fn) {
                fn(userData);
              } else {
                this.setSession(userData, userData.publisher, userData.demandSourceNameMap);
              }
            },
          )
          .catch(() => {
            this.setState({
              sessionActive: SessionInactive,
            });
            navigateError();
          });
      }
      this.setState({
        sessionActive: SessionIntrospect,
      });
      authenticate()
        .then(() => {
          return this.getSession().then(
            (userData: {
              user: IUser;
              publisher: IPublisher;
              demandSourceNameMap: IDemandSourceNameMap;
            }) => {
              const publisherID = parseLocation(this.props.location.pathname).publisher;
              if (publisherID !== undefined) {
                this.getUINotices(publisherID);
              } else {
                this.getUINotices(userData.publisher.ID);
              }
              if (fn) {
                fn(userData);
              } else {
                this.setSession(userData, userData.publisher, userData.demandSourceNameMap);
              }
            },
          );
        })
        .catch(() => {
          this.setState({
            sessionActive: SessionInactive,
          });
          navigateError();
        });
    };

    setBidderParameters = (publisherID: string) => {
      // Load Bidder Parameters
      getHeaderBidderParamOptions({
        query: {
          publisher_id: publisherID,
        },
      }).then((options: Record<string, IHeaderBidderParamOption[]>) => {
        this.props.dispatch(USER_ACTIONS_SET({ bidderParameters: options } as Partial<IUserState>));
      });
    };

    setSession = (
      userData: { user: IUser; publisher: IPublisher },
      publisher: IPublisher,
      demandSourceNameMap: IDemandSourceNameMap,
    ) => {
      identify(userData.publisher.ID, userData.user, resellerID);
      // these dispatches are ordered!
      this.props.dispatch(USER_ACTIONS_SET(userData));
      this.props.dispatch(
        SESSION_ACTIONS_SET({
          publisher,
          demandSourceNameMap,
        }),
      );

      // Load bidder parameters
      this.setBidderParameters(publisher.ID);

      // Load permissions
      getPermissionTemplate(parseInt(publisher.ID, 10))
        .then(([permissionTemplate, err]) => {
          if (err === undefined) {
            this.props.dispatch(USER_ACTIONS_SET({ permissionTemplate } as Partial<IUserState>));
            if (permissionTemplate !== undefined) {
              PermissionService.getInstance().setTemplate(permissionTemplate);
            }
          } else {
            console.error("Can't get permissions ", err);
          }
          // only render the page once the redux store is ready
          this.setState({
            sessionActive: SessionActive,
          });
        })
        .catch(() => {
          // only render the page once the redux store is ready
          this.setState({
            sessionActive: SessionActive,
          });
        });
    };

    render() {
      const currentPath = this.props.location.pathname;

      const routeTitle = getRouteTitle(currentPath);
      if (routeTitle !== '') {
        page(routeTitle, currentPath, this.props.user.user);
      }

      return (
        <BaseClass id='app'>
          <Switch>
            <Route
              path='/app/pub/:pub/reports/:report/preview'
              render={() => {
                if (
                  this.state.sessionActive === SessionUnknown ||
                  this.state.sessionActive === SessionIntrospect
                ) {
                  this.loadSession();
                  return <LoadingView active={true} text='Generating Report...' />;
                }
                if (this.state.sessionActive === SessionActive) {
                  return (
                    <Switch>
                      <Route
                        path='/app/pub/:pub/reports/:report/preview'
                        component={ReportPreviewContainer}
                      />
                      <Redirect to='/app/pub/:pub/reports/:report/preview' />
                    </Switch>
                  );
                }
                return <Redirect to='/signin' />;
              }}
            />

            <Route
              path='/app/pub/:pub?'
              render={() => {
                const loc = parseLocation(this.props.location.pathname);
                if (!loc.publisher) {
                  this.loadSession(
                    (userData: {
                      user: IUser;
                      publisher: IPublisher;
                      demandSourceNameMap: IDemandSourceNameMap;
                    }) => {
                      // TODO FIXME: can't call a setState in render()
                      this.setSession(userData, userData.publisher, userData.demandSourceNameMap);
                      if (isMobileView()) {
                        navigate(RouteMobileHome, { p: userData.publisher.ID });
                      } else {
                        navigate(
                          isIntegralplatform()
                            ? RouteIASDashboard
                            : replaceSSAILandingRoute(userData.user, RouteDashboards),
                          {
                            p: userData.publisher.ID,
                          },
                        );
                      }
                    },
                  );
                  return (
                    <LoadingView
                      active={true}
                      text='Loading your session!'
                      containerStyles={{
                        position: 'fixed',
                      }}
                      innerStyles={{
                        width: '350px',
                      }}
                    />
                  );
                } else if (
                  this.state.sessionActive === SessionUnknown ||
                  this.state.sessionActive === SessionIntrospect
                ) {
                  this.loadSession(this.loadRoute); // TODO FIXME: can't call a setState in render()
                  return (
                    <LoadingView
                      active={true}
                      text='Loading your session!'
                      containerStyles={{
                        position: 'fixed',
                      }}
                      innerStyles={{
                        width: '350px',
                      }}
                    />
                  );
                }
                if (this.state.sessionActive === SessionActive) {
                  const isSuperUser = hasRoleSuperUser(this.props.user.user);
                  const isPublicaUser = this.props.session.publisher.ID === PID.publica;

                  const isV2 = isPathV2(currentPath);
                  if (isMobileView()) {
                    return (
                      <MobileBaseView>
                        <Switch>
                          <Route component={MobileDashboard} path={RouteMobileHome.path} />
                          <Redirect
                            to={getRoutePath(RouteMobileHome, {
                              publisherID: this.props.user.publisher.ID,
                            })}
                          />
                        </Switch>
                      </MobileBaseView>
                    );
                  }

                  if (isV2) {
                    return (
                      <PublisherBaseView>
                        <Switch>
                          {!isCampaignRouteDisabled(this.props.session.publisher) && (
                            <Route
                              component={LineItemContainer}
                              path={RouteCampaignExisting.path}
                            />
                          )}
                          {!isCampaignRouteDisabled(this.props.session.publisher) && (
                            <Route component={CampaignNewView} path={RouteCampaignNew.path} />
                          )}
                          {!isCampaignRouteDisabled(this.props.session.publisher) && (
                            <Route component={CampaignCopyView} path={RouteCampaignCopy.path} />
                          )}

                          {/* Route for new bidder */}
                          <Route
                            component={BidderSettingsNewView}
                            path={RouteBidderSettingsNew.path}
                          />

                          {/* Route to edit a bidder */}
                          <Route
                            component={BidderSettingsExistingView}
                            path={RouteBidderSettingsExisting.path}
                          />

                          {/* Route to clone bidders */}
                          <Route
                            component={BidderSettingsNewView}
                            path={RouteBidderSettingsClone.path}
                          />

                          {/* Route to bulk edit bidders */}
                          <Route
                            component={BidderSettingsNewView}
                            path={RouteBidderBulkUpdate.path}
                          />

                          {/* Route to bulk edit monitors */}
                          <Route component={MonitorNewView} path={RouteMonitorBulkUpdate.path} />

                          {this.props.session.publisher.NetworkEnabled === EBool.EBool_TRUE && (
                            <Route
                              component={NetworkSettingsNewView}
                              path={RouteNetworkSettingsNew.path}
                            />
                          )}
                          {this.props.session.publisher.NetworkEnabled === EBool.EBool_TRUE && (
                            <Route
                              component={NetworkSettingsCloneView}
                              path={RouteNetworkSettingsClone.path}
                            />
                          )}
                          {this.props.session.publisher.NetworkEnabled === EBool.EBool_TRUE && (
                            <Route
                              component={NetworkSettingsExistingView}
                              path={RouteNetworkSettingsExisting.path}
                            />
                          )}
                          {isSuperUser && (
                            <Route
                              component={FreewheelSettingsView}
                              path={RouteFreewheelSiteNew.path}
                            />
                          )}
                          {isSuperUser && (
                            <Route
                              component={FreewheelSettingsView}
                              path={RouteFreewheelSiteEdit.path}
                            />
                          )}
                          <Route
                            component={ConfigurationsSettingsView}
                            path={RouteConfigurations.path}
                          />
                          <Route component={BrandSafetyNewView} path={RouteBrandSafetyNew.path} />
                          <Route component={BrandSafetyEditView} path={RouteBrandSafetyEdit.path} />
                          <Route
                            component={BrandSafetyTrackerNewView}
                            path={RouteBrandSafetyTrackerNew.path}
                          />
                          <Route
                            component={BrandSafetyTrackerEditView}
                            path={RouteBrandSafetyTrackerEdit.path}
                          />

                          {!isDealRouteDisabled(this.props.session.publisher) && (
                            <Route
                              component={DealTPAccountView}
                              path={RouteDealTPAccountList.path}
                            />
                          )}

                          {/* TODO: once the deal settings view is ready,
                         replace the old deal create/edit views with the new ones below */}
                          {/* {!isDealRouteDisabled(this.props.session.publisher) && (
                            <Route exact component={DealCreate} path={RouteDealCreate.path} />
                          )}
                          {!isDealRouteDisabled(this.props.session.publisher) && (
                            <Route exact component={DealNew} path={RouteDealNew.path} />
                          )}
                          {!isDealRouteDisabled(this.props.session.publisher) && (
                            <Route exact component={DealExisting} path={RouteDealEdit.path} />
                          )} */}

                          {!isDealRouteDisabled(this.props.session.publisher) && (
                            <Route exact component={DealCreateView} path={RouteDealCreate.path} />
                          )}
                          {!isDealRouteDisabled(this.props.session.publisher) && (
                            <Route exact component={DealSettingsView} path={RouteDealNew.path} />
                          )}
                          {!isDealRouteDisabled(this.props.session.publisher) && (
                            <Route exact component={DealSettingsView} path={RouteDealEdit.path} />
                          )}
                          {!isDealRouteDisabled(this.props.session.publisher) && (
                            <Route exact component={DealSettingsView} path={RouteDealClone.path} />
                          )}

                          <Route component={BidderSiteNewView} path={RouteChannelNew.path} />
                          <Route
                            component={SiteSettingsListView}
                            path={RouteChannelExisting.path}
                          />
                          <Route
                            component={SiteSettingsListView}
                            path={RouteChannelBulkUpdate.path}
                          />
                          <Route component={ChannelsView} path={RouteChannelsView.path} />
                          <Route
                            component={OrderSettingsView}
                            path={RouteOrderSettingsExisting.path}
                          />
                          <Route component={OrderSettingsView} path={RouteOrderSettingsNew.path} />
                          <Route
                            component={OrderSettingsView}
                            path={RouteOrderSettingsClone.path}
                          />
                          <Route component={IPTraceNewView} path={RouteIPTraceNew.path} />
                          <Route component={IPTraceListView} path={RouteIPTraceList.path} />
                          <Route component={MonitorNewView} path={RouteMonitorNew.path} />
                          <Route component={MonitorExistingView} path={RouteMonitorEdit.path} />
                          <Route component={MonitorNewView} path={RouteMonitorBulkUpdate.path} />
                          <Route
                            component={MonitorBulkUploadView}
                            path={RouteMonitorBulkUpload.path}
                          />
                          {(isSuperUser || isForecastingAllowed(this.props.session.publisher)) && (
                            <Route component={ForecastingView} path={RouteForecasting.path} />
                          )}
                          {(isSuperUser || !isForecastingAllowed(this.props.session.publisher)) && (
                            <Route
                              component={InventoryForecastView}
                              path={RouteInventoryForecast.path}
                            />
                          )}
                          <Route
                            component={RouteSettingsNewView}
                            path={RouteInventoryRouteNew.path}
                          />
                          <Route
                            component={RouteSettingsExistingView}
                            path={RouteInventoryRouteEdit.path}
                          />
                          <Route
                            component={ContextualSegmentSettingsNewView}
                            path={RouteContextualSegmentNew.path}
                          />
                          <Route
                            component={ContextualSegmentSettingsExistingView}
                            path={RouteContextualSegmentExisting.path}
                          />
                          {/* TARGETING */}
                          <Route
                            component={TargetingPresetFormView}
                            path={RouteTargetingPresetClone.path}
                          />
                          <Route
                            component={TargetingPresetFormView}
                            path={RouteTargetingPresetNew.path}
                          />
                          <Route
                            component={TargetingPresetFormView}
                            path={RouteTargetingPresetExisting.path}
                          />
                          <Route component={TargetingPresets} path={RouteTargetingList.path} />

                          {/* INVENTORY RULES */}
                          {(isSuperUser ||
                            shouldDisplayInventoryRules(this.props.session.publisher)) && (
                            <Route
                              component={InventoryRulesList}
                              path={RouteInventoryRulesList.path}
                            />
                          )}
                          <Route component={AdBreaksList} path={RouteAdBreaksList.path} />
                          <Route
                            component={ContentMetadataList}
                            path={RouteContentMetadataList.path}
                          />
                          <Route component={CustomDataList} path={RouteCustomDataList.path} />
                          <Redirect
                            to={getRoutePath(
                              isIntegralplatform()
                                ? RouteIASDashboard
                                : replaceSSAILandingRoute(this.props.user.user, RouteDashboards),
                              { publisherID: this.props.user.publisher.ID },
                            )}
                          />
                        </Switch>
                      </PublisherBaseView>
                    );
                  }

                  return (
                    <PublisherBaseView>
                      <Switch>
                        <Route component={DashboardHome} path={RouteDashboards.path} />
                        <Route component={ReportBuilderNew} path={RouteReportNew.path} />
                        <Route component={ReportBuilderExisting} path={RouteReportExisting.path} />
                        <Route component={ReportingListView} path={RouteReportsList.path} />
                        <Route
                          component={BidderSettingsNewView}
                          path={RouteBidderBulkUpdate.path}
                        />
                        <Route component={BidderBulkUploadView} path={RouteBidderBulkUpload.path} />
                        <Route component={Bidders} path={RouteBidderSettingsList.path} />
                        <Route component={AccountSettingsView} path={RouteAccountSettings.path} />
                        <Route
                          component={PublisherSettingsView}
                          path={RoutePublisherSettings.path}
                        />
                        <Route
                          component={PublisherChangeLogsView}
                          path={RoutePublisherChangeLogs.path}
                        />
                        <Route component={TeamSettingsView} path={RouteTeamSettings.path} />
                        {!isCampaignRouteDisabled(this.props.session.publisher) && (
                          <Route
                            component={LineItemsListView}
                            path={RouteCampaignSettingsList.path}
                          />
                        )}
                        {!isCampaignRouteDisabled(this.props.session.publisher) && (
                          <Route
                            component={CreativeLibraryListView}
                            path={RouteCreativesLibraryList.path}
                          />
                        )}
                        {!isAudienceRouteDisabled(this.props.session.publisher) && (
                          <Route
                            component={AudienceManagerSegmentNewView}
                            path={RouteAudienceManagerSegmentNew.path}
                          />
                        )}
                        {!isAudienceRouteDisabled(this.props.session.publisher) && (
                          <Route
                            component={AudienceManagerCombineSegmentsView}
                            path={RouteAudienceManagerCombineSegments.path}
                          />
                        )}
                        {!isAudienceRouteDisabled(this.props.session.publisher) && (
                          <Route
                            component={AudienceManagerDataProviderSettings}
                            path={RouteAudienceManagerDataProviderSettings.path}
                          />
                        )}
                        {!isAudienceRouteDisabled(this.props.session.publisher) && (
                          <Route
                            component={AudienceManagerSegmentExistingView}
                            path={RouteAudienceManagerSegmentExisting.path}
                          />
                        )}
                        {isLiverampIntegrationEnabled(
                          this.props.session.publisher.LiverampDistributionManagerID,
                        ) && (
                          <Route
                            component={LiverampMarketplaceSegmentsListView}
                            path={RouteAudienceManagerLiverampMarketplace.path}
                          />
                        )}
                        {!isAudienceRouteDisabled(this.props.session.publisher) && (
                          <Route
                            component={AudienceSegments}
                            path={RouteAudienceManagerSettingsList.path}
                          />
                        )}
                        {isSuperUser && (
                          <Route
                            component={AudienceDataShareListView}
                            path={RouteAudienceDataShareManager.path}
                          />
                        )}
                        {isLiverampIntegrationEnabled(
                          this.props.session.publisher.LiverampDistributionManagerID,
                        ) && (
                          <Route
                            component={ManageLiveRampDistribution}
                            path={RouteSegmentsLiverampDistribution.path}
                          />
                        )}
                        <Route
                          component={ContextualSegmentSettingsListView}
                          path={RouteContextualSegmentSettingsList.path}
                        />
                        <Route
                          component={RouteSettingsListView}
                          path={RouteInventoryRouteList.path}
                        />
                        {isSuperUser && isPublicaUser && (
                          <Route component={SSPReportNewView} path={RouteSSPReportNew.path} />
                        )}
                        {isSuperUser && isPublicaUser && (
                          <Route
                            component={SSPReportExistingView}
                            path={RouteSSPReportExisting.path}
                          />
                        )}
                        {isSuperUser && isPublicaUser && (
                          <Route component={SSPReportListView} path={RouteSSPReportList.path} />
                        )}
                        {isSuperUser && (
                          <Route
                            component={IABCategoryManagerView}
                            path={RouteIABCategoryManager.path}
                          />
                        )}
                        {isSuperUser && (
                          <Route component={ResellerNewView} path={RouteResellerNew.path} />
                        )}
                        {isSuperUser && (
                          <Route
                            component={ResellerExistingView}
                            path={RouteResellerExisting.path}
                          />
                        )}
                        {isSuperUser && (
                          <Route
                            component={ResellerListView}
                            path={RouteResellerManagerList.path}
                          />
                        )}
                        {(isSuperUser ||
                          !isPlatformConnectDisabled(this.props.session.publisher)) && (
                          <Route
                            component={PlatformConnectOfferNewContainer}
                            path={RoutePlatformConnectOfferNew.path}
                          />
                        )}
                        {(isSuperUser ||
                          !isPlatformConnectDisabled(this.props.session.publisher)) && (
                          <Route
                            component={PlatformConnectView}
                            path={RoutePlatformConnects.path}
                          />
                        )}
                        {this.props.session.publisher.NetworkEnabled === EBool.EBool_TRUE && (
                          <Route component={NetworkHomeView} path={RouteNetworkDashboard.path} />
                        )}
                        {this.props.session.publisher.NetworkEnabled === EBool.EBool_TRUE && (
                          <Route component={NetworkListView} path={RouteNetworkList.path} />
                        )}
                        {isSuperUser && (
                          <Route component={FreewheelListView} path={RouteFreewheelSiteList.path} />
                        )}
                        <Route component={BrandSafetyListView} path={RouteBrandSafetyList.path} />
                        <Route component={DealsListView} path={RouteDealList.path} />
                        <Route component={OrdersListView} path={RouteOrderList.path} />
                        <Route component={MonitorListView} path={RouteMonitorList.path} />
                        <Route
                          component={MeasurementHomeView}
                          path={RouteMeasurementDashboard.path}
                        />
                        <Route component={MeasurementDrill} path={RouteMeasurementDrill.path} />
                        {isSuperUser && (
                          <Route
                            component={BillingDetailListView}
                            path={RouteBillingDetailList.path}
                          />
                        )}
                        {isSuperUser && (
                          <Route
                            component={BillingDetailConfigurationSettingsView}
                            path={RouteBillingDetailConfigurationNew.path}
                          />
                        )}
                        {isSuperUser && (
                          <Route
                            component={BillingDetailConfigurationSettingsView}
                            path={RouteBillingDetailConfigurationExisting.path}
                          />
                        )}
                        {isSuperUser && (
                          <Route
                            component={BillingDetailTiersSettingsView}
                            path={RouteBillingDetailTiersNew.path}
                          />
                        )}
                        {isSuperUser && (
                          <Route
                            component={BillingDetailTiersSettingsView}
                            path={RouteBillingDetailTiersExisting.path}
                          />
                        )}
                        <Route component={InventoryRulesView} path={RouteInventoryRules.path} />
                        <Route component={BrandsDashboard} path={RouteBrandsDashboard.path} />
                        {isSuperUser && <Route component={Sandbox} path={RouteSandbox.path} />}
                        {isSuperUser && <Route component={AdVisor} path={RouteAdVisor.path} />}
                        {(isSuperUser || isDemoAccount(this.props.user.user.Username)) && (
                          <Route component={DashboardHome} path={RouteDashboards.path} />
                        )}
                        <Route component={IASDashboard} path={RouteIASDashboard.path} />
                        <Route component={IASGlossary} path={RouteIASGlossary.path} />
                        <Route component={ReportsDashboard} path={RouteReportsDashboard.path} />
                        <Route component={IasReportView} path={RouteReportBuilderNew.path} />
                        <Route component={IasReportView} path={RouteReportBuilderExisting.path} />
                        <Route
                          // TODO: create reports list view to replace ReportsDashboard
                          component={ReportsDashboard}
                          path={RouteReportBuilderList.path}
                        />
                        <Route
                          component={BulkUpdateChannelGroups}
                          path={RouteChannelGroupBulkUpdate.path}
                        />
                        <Redirect
                          to={getRoutePath(
                            isIntegralplatform()
                              ? RouteIASDashboard
                              : replaceSSAILandingRoute(this.props.user.user, RouteDashboards),
                            { publisherID: this.props.user.publisher.ID },
                          )}
                        />
                      </Switch>
                    </PublisherBaseView>
                  );
                }
                return <Redirect to='/signin' />;
              }}
            />

            <Redirect to='/signin' />
          </Switch>
        </BaseClass>
      );
    }
  },
);

interface IProps2 extends IHistoryProps {}

interface IState {
  isConsoleDown: boolean;
}

const BaseView = decorate(
  {
    router: true,
  },
  class extends React.Component<IProps2, IState> {
    state = {
      isConsoleDown: false,
    };

    updateConsoleDown() {
      this.setState({
        isConsoleDown: true,
      });
    }

    render() {
      if (this.state.isConsoleDown) {
        return <Route path='/' component={ConsoleDownView} />;
      }

      return (
        <Switch>
          {/* Signup, ... */}
          <Route
            path='/signin'
            render={(props) => (
              <SigninView {...props} updateConsoleDown={this.updateConsoleDown.bind(this)} />
            )}
          />
          <Route path='/accept-invitation' component={InvitationAcceptionView} />
          <Route path='/forgot-password' component={PasswordRecoverView} />
          <Route path='/reset-password' component={PasswordResetView} />
          <Route path='/app' component={AppRouterView} />
          <Route
            path='/error'
            render={(props) => (
              <AppErrorView {...props} updateConsoleDown={this.updateConsoleDown.bind(this)} />
            )}
          />
          <Redirect from='/' to='/signin' />
        </Switch>
      );
    }
  },
);

export function AppView() {
  loadAnalytics(); // load analytics

  return (
    <Suspense fallback='... is loading'>
      <ThemeProvider theme={theme}>
        <Provider store={store}>
          <HashRouter>
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <CssBaseline />
              <ErrorBoundary>
                <BaseView />
              </ErrorBoundary>
            </MuiPickersUtilsProvider>
          </HashRouter>
        </Provider>
      </ThemeProvider>
    </Suspense>
  );
}
